import React, { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'
import * as echarts from 'echarts'

interface DataPoint {
  date?: string
  value?: number
  hour?: number
  month?: number
}

interface CalDataPoint {
  [month: number]: number[]
}

interface Props {
  data?: DataPoint[] | CalDataPoint
  month: number
  width?: string
  height?: string
  selectedChartDataType?: string
  showDataZoom?: boolean
  onPointClick?: (point: DataPoint) => void
}

interface WrapperProps {
  height?: string
  width?: string
  fontSize?: string
}

const Wrapper = styled.div<WrapperProps>`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: ${(props) => (props.height ? props.height : '100%')};
  width: ${(props) => (props.width ? props.width : '100%')};
  padding: 0;
  flex-shrink: 0;
`

const HOUR_LABEL = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
  '10',
  '11',
  '12',
  '13',
  '14',
  '15',
  '16',
  '17',
  '18',
  '19',
  '20',
  '21',
  '22',
  '23',
]

const MonthLineGraph2 = (props: Props) => {
  const {
    data,
    month,
    width,
    height,
    selectedChartDataType,
    showDataZoom = true,
    onPointClick,
  } = props
  const filteredData = useMemo(() => {
    if (Array.isArray(data))
      return data.filter((item) => {
        if (!item.date) return false

        const itemDate = new Date(item.date)
        return itemDate.getMonth() + 1 === month
      })
    return []
  }, [data, month])

  const calData = useMemo(() => {
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const monthData = data[month] // 해당 월의 데이터 가져오기
      if (monthData && Array.isArray(monthData)) {
        return monthData
      }
    }
    return []
  }, [data, month])

  const { modifiedTimeList, valueList } = useMemo(() => {
    const timeList: string[] = []
    const values: number[] = []

    filteredData.forEach((item) => {
      if (item.date !== undefined && item.value !== undefined) {
        const modifiedTime = item.date.slice(2, 16).replace('T', ' ')
        timeList.push(modifiedTime)
        values.push(item.value)
      }
    })

    return {
      modifiedTimeList: timeList,
      valueList: values,
    }
  }, [filteredData])

  const { FinalLabel, FinalData } = useMemo(() => {
    if (selectedChartDataType === 'realTime') {
      return {
        FinalLabel: modifiedTimeList,
        FinalData: valueList,
      }
    }

    if (
      selectedChartDataType === 'aver' ||
      selectedChartDataType === 'mid' ||
      selectedChartDataType === 'mode'
    ) {
      return {
        FinalLabel: HOUR_LABEL,
        FinalData: calData,
      }
    }

    return {
      FinalLabel: [],
      FinalData: [],
    }
  }, [calData, modifiedTimeList, selectedChartDataType, valueList])

  const chartRef = useRef<echarts.ECharts | null>(null)
  const chartId = useRef(`chart-${Math.floor(Math.random() * 1000000)}`)

  useEffect(() => {
    const chartDom = document.getElementById(chartId.current)
    if (chartDom) {
      if (chartRef.current) {
        chartRef.current.dispose()
      }

      const chart = echarts.init(chartDom)

      const option: echarts.EChartsOption = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            label: {
              backgroundColor: '#6a7985',
            },
          },
        },
        grid: {
          // [수정] 상단에 'kW' 글자가 들어갈 공간 확보 (30~40px 추천)
          top: 35,
          left: '3%',
          right: '3%',
          bottom: showDataZoom ? '20%' : '8%',
          // height: '70%', // [삭제] top과 bottom이 있으면 height는 자동 계산됩니다.
          containLabel: true, // [추가] 라벨이 잘리지 않도록 포함해서 계산
        },
        yAxis: {
          scale: true,
          name: 'kWh',
          nameLocation: 'end', // (선택) 이름 위치 명시 (기본값이 end이긴 함)
          nameTextStyle: {
            align: 'right', // 우측 정렬
            padding: [0, 0, 0, 0], // 패딩 조정 (필요시)
            fontWeight: 'bold', // 잘 보이게 굵게
            color: '#666',
          },
        },
        xAxis: {
          type: 'category',
          data: FinalLabel,
        },
        dataZoom: showDataZoom
          ? [
              {
                type: 'slider',
              },
            ]
          : undefined,
        series: [
          {
            data: FinalData,
            type: 'line',
            animation: false,
          },
        ],
      }

      const handleResize = () => {
        chart.resize() // 차트 크기 조정
      }

      window.addEventListener('resize', handleResize)

      chart.setOption(option)
      chart.on('click', (params) => {
        if (!onPointClick || typeof params.dataIndex !== 'number') return

        const clickedPoint = filteredData[params.dataIndex]
        if (clickedPoint) {
          onPointClick(clickedPoint)
        }
      })
      chartRef.current = chart

      return () => {
        if (chartRef.current) {
          chartRef.current.off('click')
          chartRef.current.dispose()
        }
        window.removeEventListener('resize', handleResize)
      }
    }
  }, [
    filteredData,
    FinalData,
    FinalLabel,
    selectedChartDataType,
    showDataZoom,
    onPointClick,
  ])

  return (
    <Wrapper height={height} width={width}>
      <div id={chartId.current} style={{ height: height, width: width }} />
    </Wrapper>
  )
}

export default MonthLineGraph2
